import React, { useState } from 'react';
import { GameScreenConfiguration } from '@luckycart/xpn-types';

import { GameHtmlZone, GameZone, VideoGameZone } from './GameScreen.style';
import { 
  OneArmedBanditPlugin,
  WheelPlugin,
  ScratchPlugin,
  PullOffPlugin,
  DartPlugin,
  PinataPlugin,
  EasterPlugin,
  ChristmasPlugin,
  BalloonsPlugin,
  SchoolBoardPlugin,
  CustomVideoPlugin,
} from '../game-plugins/GamePlugins';
import { Background } from '../common-components/Background';
import { BlockInfoType } from '../../common-types/BlockInfo.type';

const MILLISECONDS_BEFORE_LAUNCHING_GAME_AFTER_LOAD = 500;

interface Props {
  config: GameScreenConfiguration;
  onGameBegin?: () => void;
  onGameDone?: () => void;
  addEditableBlock?: (block: BlockInfoType) => void;
  removeEditableBlock?: (configPath: string) => void;
  isEdition?: boolean;
  onBlockEdit?: React.MouseEventHandler<HTMLDivElement>;
  hasWon?: boolean | null;
}

export const GameScreen: React.FunctionComponent<Props> = ({
  addEditableBlock,
  removeEditableBlock,
  config,
  onGameBegin,
  onGameDone,
  isEdition,
  onBlockEdit,
  hasWon,
}: Props) => {
  const [isPlaying, setIsPlaying] = useState<boolean>(false);

  const onPluginLoaded = (): void => {
    if (isEdition === true) {
      return;
    }
    setTimeout(() => {
      setIsPlaying(true);
      if (onGameBegin) {
        onGameBegin();
      }
    }, MILLISECONDS_BEFORE_LAUNCHING_GAME_AFTER_LOAD);
  };

  const handleEndOfPlugin = (): void => {
    if (onGameDone) {
      onGameDone();
    }
  };

  const commonPluginProps = {
    hasWon:
      hasWon === undefined
        ? null
        : hasWon,
    onPluginDone: handleEndOfPlugin,
    addEditableBlock,
    removeEditableBlock,
    onBlockEdit,
    isPlaying,
    isEdition: isEdition === true,
    onLoad: onPluginLoaded,
    winVideo: config.plugin.type === 'customVideo' && config.plugin.plugins.video !== undefined ? config.plugin.plugins.video.win : { mp4: '' },
    loseVideo: config.plugin.type === 'customVideo' && config.plugin.plugins.video !== undefined ? config.plugin.plugins.video.lose : { mp4: '' },
    landingVideo: config.plugin.type === 'customVideo' && config.plugin.plugins.video !== undefined ? config.plugin.plugins.video.lose : { mp4: '' },
  };

  return (
    <Background 
      displayName="Background"
      sizeInfo={{ height: 1200, width: 1200 }}
      addEditableBlock={addEditableBlock}
      dataTemplateConfig="screens.game.background"
      backgroundImage={config.background.imageUrl}
      backgroundColor={config.background.color}
      isEdition={isEdition}
      onClick={onBlockEdit}
    >
     
      {config.plugin.type === 'wheel' && config.plugin.plugins.wheel !== undefined &&
         <GameZone>
           <WheelPlugin
             config={config.plugin.plugins.wheel}
             {...commonPluginProps}
           />
         </GameZone>
      }
      {config.plugin.type === 'scratch' && config.plugin.plugins.scratch !== undefined &&
      <GameZone>
        <ScratchPlugin
          config={config.plugin.plugins.scratch}
          {...commonPluginProps}
        />
      </GameZone>
      }
      {config.plugin.type === 'armedBandit' && config.plugin.plugins.armedBandit !== undefined &&
      <GameZone>
        <OneArmedBanditPlugin
          config={config.plugin.plugins.armedBandit}
          {...commonPluginProps}
        />
      </GameZone>
      }
      {config.plugin.type === 'pullOff' &&
      <VideoGameZone>
        <PullOffPlugin
          {...commonPluginProps}
        />
      </VideoGameZone>
      }
      {config.plugin.type === 'dart' &&
      <VideoGameZone>
        <DartPlugin
          {...commonPluginProps}
        />
      </VideoGameZone>
      }
      {config.plugin.type === 'pinata' &&
      <VideoGameZone>
        <PinataPlugin
          {...commonPluginProps}
        />
      </VideoGameZone>
      }
      {config.plugin.type === 'easter' &&
      <VideoGameZone>
        <EasterPlugin
          {...commonPluginProps}
        />
      </VideoGameZone>
      }
      {config.plugin.type === 'christmas' &&
      <VideoGameZone>
        <ChristmasPlugin
          {...commonPluginProps}
        />
      </VideoGameZone>
      }
      {config.plugin.type === 'balloons' &&
      <VideoGameZone>
        <BalloonsPlugin
          {...commonPluginProps}
        />
      </VideoGameZone>
      }
      {config.plugin.type === 'schoolboard' &&
      <VideoGameZone>
        <SchoolBoardPlugin
          {...commonPluginProps}
        />
      </VideoGameZone>
      }
      {config.plugin.type === 'customVideo' &&
      <VideoGameZone>
        <CustomVideoPlugin
          {...commonPluginProps}
        />
      </VideoGameZone>
      }      

      <GameHtmlZone
        displayName="HTML Zone"
        addEditableBlock={addEditableBlock}
        dataTemplateConfig="screens.game.mainTextBox"
        html={config.mainTextBox.html}
        isEdition={isEdition}
        onClick={onBlockEdit}
      />
    </Background>
  );
};
